import React, { useEffect } from "react";
import { Box, Button, CircularProgress, Divider, TextField, Typography } from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { DataLayout } from "./DataLayout";
import { useCommentsAndLogs } from "../hooks/useCommentsAndLogs";
import { formatDate } from "../utils/formatDate";

interface CommentsProps {
    topic: string[];
    sub_event: string[];
    object_id: string;
    parent_id: string | undefined;
    isDisabled?: boolean;
};

export const Comments: React.FC<CommentsProps> = ({ topic, sub_event, object_id, parent_id, isDisabled = false }) => {

    const [comment, setComment] = React.useState<string>("");

    const {
        isCommentsPending, comments: fetchedComments,
        getComments, postComment, isAddingComment
    } = useCommentsAndLogs({ topic, sub_event, object_id, parent_id });

    const handleSaveComment = () => {
        postComment(comment);
        setComment("");
    };

    useEffect(() => {
        getComments();
    }, []);

    if (isCommentsPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    return (
        <Box display={'flex'} flexDirection={'column'} height={'100%'}>
            <Box flex={1} display={'flex'} flexDirection={'column'} gap={2} sx={{ overflowY: 'auto' }}>
                {
                    fetchedComments.length === 0 ?
                        <Typography variant="body2" color="text.secondary" textAlign="center" mt={5}>No comments found.</Typography> :
                        fetchedComments.map((value: { user_name: string; created_date: string; comment_text: string }, index: React.Key | null | undefined) => (
                            <Box key={index} display={'flex'} alignItems={'start'} justifyContent={'start'} flexDirection={'column'} bgcolor={'action.hover'} width={'100%'} p={1.5} borderRadius={1}>
                                <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} width={'100%'}>
                                    <Typography variant="caption" color="text.primary">
                                        {value.user_name}
                                    </Typography>
                                    <Typography variant="caption" color="text.primary">
                                        {formatDate(value.created_date)}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                    {value.comment_text}
                                </Typography>
                            </Box>
                        ))
                }
            </Box>
            {
                !isDisabled && (
                    <Box mt={5}>
                        <DataLayout title={"Add Comment"} bgVariant='filled'>
                            <Box p={2}>
                                <TextField
                                    fullWidth
                                    label="Comment"
                                    value={comment}
                                    type="note"
                                    size="small"
                                    onChange={(event) => setComment(event.target.value)}
                                    placeholder="Enter your notes"
                                    required
                                />
                                <Divider sx={{ my: 2 }} />
                                <Box display={'flex'} justifyContent={'flex-end'} gap={2}>
                                    <Button variant="text" sx={{ color: 'text.primary' }} loading={isAddingComment} disabled={isAddingComment || !comment} onClick={() => setComment("")}>Cancel</Button>
                                    <Button variant="text" sx={{ color: 'primary.main' }} loading={isAddingComment} disabled={isAddingComment || !comment} startIcon={<CheckCircleOutlineIcon />} onClick={handleSaveComment}>add comment</Button>
                                </Box>
                            </Box>
                        </DataLayout>
                    </Box>
                )
            }
        </Box>
    )
}